<template>
  <div class="chart-container row">
    <div class="chart">
      <highcharts
        v-if="chartOptions"
        :key="chartKey"
        :options="chartOptions"
      />
    </div>
    <div class="status">
      <div>
        <div class="line">
          <div class="line-title">{{ t('sereignchart_stadaupphaf') }}</div>
          <div class="amount">{{ currency(statusBegin) }} kr.</div>
        </div>

        <div class="line">
          <div class="line-title">{{ t('sereignchart_hreyfingar') }}</div>
          <div class="amount">
            {{ currency(totalMovements) }} kr.
          </div>
        </div>

        <div class="line">
          <div class="line-title">{{ t('sereignchart_avoxtun') }}</div>
          <div class="amount">{{ currency(ret) }} kr.</div>
        </div>
      </div>

      <div class="sum">
        <div>{{ t('sereignchart_stadalok') }}</div>
        <div class="amount">{{ currency(statusEnd) }} kr.</div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useCurrency } from '@/composables/useCurrency'

type Movement = { dags: string; upphaed: number }

const props = defineProps<{
  statusBegin: number
  statusEnd: number
  movements: Movement[]
  ret: number | undefined // renamed from "return" to avoid keyword name; mapped below
  startingMonth: number
  currentChosenDate: string
}>()

// Back-compat: if parent still sends prop "return", accept via withDefaults in parent or map here:
const ret = computed(() => Number((props as any).return ?? props.ret ?? 0))

const { t } = useI18n()
const { currency } = useCurrency()

const chartKey = ref(0)

const monthNames: Record<number, string> = {
  0: 'Jan', 1: 'Feb', 2: 'Mar', 3: 'Apr',
  4: 'Maí', 5: 'Jún', 6: 'Júl', 7: 'Ágú',
  8: 'Sep', 9: 'Okt', 10: 'Nóv', 11: 'Des'
}

const chosenYear = computed(() => {
  const d = new Date()
  d.setFullYear(Number(props.currentChosenDate))
  return d.getFullYear()
})

const isCurrentYear = computed(() => chosenYear.value === new Date().getFullYear())

const months = computed(() => {
  const out: Date[] = []
  if (isCurrentYear.value) {
    // last 12 months
    for (let i = 12; i > 0; i--) {
      const m = new Date()
      m.setDate(1)
      m.setMonth(m.getMonth() - i)
      out.push(m)
    }
  } else {
    for (let i = 0; i <= 11; i++) {
      const d = new Date()
      d.setDate(1)
      d.setMonth(i)
      d.setFullYear(chosenYear.value)
      out.push(d)
    }
  }
  return out
})

const categories = computed(() => months.value.map(m => monthNames[m.getMonth()]))

const seriesData = computed<number[] | (number | null)[]>(() => {
  const movements = Array.isArray(props.movements) ? props.movements : []
  const values: (number | null)[] = []

  let usedBegin = false
  let justAddedBegin = false

  for (let i = 0; i < months.value.length; i++) {
    justAddedBegin = false
    const m = months.value[i]
    const monthMovs = movements.filter(x => {
      const d = new Date(x.dags)
      return d.getMonth() === m.getMonth() && d.getFullYear() === m.getFullYear()
    })
    const monthSum = monthMovs.reduce((acc, x) => acc + Number(x.upphaed), 0)

    let addition: number | null = monthSum

    if (i === 0 && monthMovs.length > 0 && !usedBegin) {
      addition += props.statusBegin
      usedBegin = true
      justAddedBegin = true
    } else if (i > 0 && monthMovs.length > 0 && !usedBegin) {
      values[i - 1] = props.statusBegin
      addition += (values[i - 1] as number)
      usedBegin = true
      justAddedBegin = true
    }

    if (monthMovs.length > 0 && usedBegin && !justAddedBegin) {
      addition += (values[i - 1] as number)
    } else if (!justAddedBegin) {
      if ((values[i - 1] as number) > 0) {
        addition = values[i - 1] as number
      } else {
        addition = null
      }
    }

    values.push(addition)
  }

  // bump last increase with return
  for (let i = months.value.length; i > 0; i--) {
    const cur = values[i] as number | null
    const prev = values[i - 1] as number | null
    if (cur == null || prev == null) continue
    if (cur === prev) {
      values[i] = null
    } else if (cur > prev) {
      values[i] = cur + ret.value
      break
    }
  }

  return values
})

const totalMovements = computed(() =>
  (props.movements || []).reduce((a, { upphaed }) => a + Number(upphaed), 0)
)

const chartOptions = computed(() => ({
  title: { text: '' },
  xAxis: [{
    categories: categories.value,
    labels: { style: { fontSize: '14px' } }
  }],
  yAxis: [{
    labels: {
      formatter: function (this: any) {
        // @ts-ignore - Highcharts is global in project
        return Highcharts.numberFormat(this.value / 1000, 0, ',', '.') + 'þ. kr'
      },
      style: { fontSize: '14px' }
    },
    title: { text: null }
  }],
  tooltip: {
    formatter: function (this: any) {
      // @ts-ignore
      return '<b>' + this.x + '</b><br/>' + Highcharts.numberFormat(this.y, 0, ',', '.') + ' kr'
    }
  },
  legend: { enabled: false },
  series: [{
    name: '',
    data: seriesData.value,
    // keep in sync with brand primary
    color: '#00877C',
    marker: { enabled: false },
    lineWidth: 3
  }]
}))

watch(seriesData, () => { chartKey.value++ })
</script>

<style lang="scss" scoped>
@use "@/scss/settings/variables" as var;

.chart-container {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;

  .chart { min-width: 65%; }

  .status {
    min-width: 389px;

    .line, .sum {
      display: flex;
      justify-content: space-between;
      padding-bottom: 16px;

      .amount { font-weight: var.$font-weight-bold; }
    }

    .sum {
      border-top: 2px solid #00877C;
      font-weight: var.$font-weight-bold;
      padding-top: 16px;
      font-size: 20px;
    }
  }

  @media (max-width: 1200px) {
    .chart {
      width: 100%;
      min-width: 100%;
    }
    .status {
      margin-top: 48px;
      border-top: 1px solid #E6E6E6;
      padding-top: 11px;
      width: 100%;
      min-width: 100%;
    }
  }
}
</style>
